// Modal drill-down for a single discrepancy: the involved record(s), the guard
// evaluation nearest in time, and a plain-language explanation of why the
// detector flagged it (thresholds mirror server/telemetry/src/detectors.ts).

import type { Discrepancy, GuardEvaluation, TripReport } from "../types.js";

interface Props {
  discrepancy: Discrepancy;
  report: TripReport;
  onClose: () => void;
}

// Why-flagged copy, aligned with the detector thresholds. Keep in sync if the
// backend tuning constants change.
const explain: Record<string, string> = {
  "premature-advance": "Stepped to the next maneuver while still > 40 m short of the current one.",
  "late-advance": "Held the step until the car was > 25 m PAST the maneuver.",
  "false-reroute": "Reroute fired while essentially on-route (off-route < 20 m) - likely GPS jitter.",
  "missed-reroute": "Car sat > 60 m off-route for 3+ consecutive samples with no reroute in the 15 s window.",
  "tts-out-of-sync": "Spoken cue fired at the wrong distance (execute > 120 m out, or prepare < 20 m in).",
  "polyline-stale": "Route geometry older than 8 s while the car was moving (> 1 m/s).",
  "distance-rounding-wrong": "Banner distance disagreed with the expected rounding by > 15 m.",
  "zoom-cascade-off": "Map zoom drifted > 1 level from the speed->zoom cascade.",
  "origin-marker-regenerated": "The origin marker id changed mid-trip (destroyed + recreated).",
  "bearing-gate-wrong": "Bearing gate accepted a heading it should have rejected (or vice versa).",
};

/** Find the guard evaluation closest in time to the discrepancy. */
function nearestGuard(report: TripReport, ts: number): GuardEvaluation | null {
  let best: GuardEvaluation | null = null;
  let bestDelta = Infinity;
  for (const g of report.records.guardEvaluations) {
    const delta = Math.abs(g.ts - ts);
    if (delta < bestDelta) {
      bestDelta = delta;
      best = g;
    }
  }
  return best;
}

export function DiscrepancyDetail({ discrepancy, report, onClose }: Props) {
  const guard = nearestGuard(report, discrepancy.ts);

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <header>
          <strong>{discrepancy.detector}</strong>
          <span className={`sev ${discrepancy.severity}`}>{discrepancy.severity}</span>
        </header>

        <p className="muted">
          {new Date(discrepancy.ts).toLocaleString()}
          {discrepancy.lat != null && discrepancy.lng != null
            ? ` · ${discrepancy.lat.toFixed(5)}, ${discrepancy.lng.toFixed(5)}`
            : " · no position"}
        </p>

        <h4>Why it was flagged</h4>
        <p>{explain[discrepancy.detector] ?? "Detector-specific rule (see detectors.ts)."}</p>

        <h4>Detector detail</h4>
        <pre className="detail">{JSON.stringify(discrepancy.detail, null, 2)}</pre>

        <h4>Nearest guard evaluation</h4>
        {guard ? (
          <pre className="detail">{JSON.stringify(guard, null, 2)}</pre>
        ) : (
          <p className="muted">No guard evaluations recorded for this trip.</p>
        )}

        <div style={{ textAlign: "right", marginTop: 12 }}>
          <button onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
